// A single place to take a cross-tab Web Lock (issue #3517).
// Several tabs of the app share one IndexedDB download queue and one org hymnal refresh, so only
// one tab may run them at a time. Web Locks are available in every browser & WebView we target
// (Chrome 69+, Safari 15.4+); the fallback covers jsdom (tests) and very old WebViews, where
// running the callback directly restores the previous single-tab behavior.
//
// By default a tab that cannot take the lock waits for it. With `ifAvailable` it skips the work
// instead and reports `ran: false`, which is what a shared queue wants: another tab is already
// draining it, so waiting for the lock would stall this tab for as long as that tab keeps working.
export async function withWebLock<T>(
  name: string,
  callback: () => Promise<T>,
  { ifAvailable }: { ifAvailable?: boolean } = {},
): Promise<{ ran: boolean; value?: T }> {
  const lockManager: LockManager | undefined = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!lockManager) {
    return { ran: true, value: await callback() };
  }
  if (!ifAvailable) {
    return { ran: true, value: await lockManager.request(name, async () => await callback()) };
  }
  let ran = false;
  const value = await lockManager.request(name, { ifAvailable: true }, async (lock) => {
    if (!lock) {
      return undefined;
    }
    ran = true;
    return await callback();
  });
  return { ran, value: value as T | undefined };
}
